import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { supabase } from '../../supabaseClient';
import { getLoginPrefill } from '../../utils/loginPrefill';
import { resolveAdminRole } from '../../utils/adminRole';
import { recordUserIp, getUserIp } from '../../utils/userIp';
import logo from '../../assets/finalclipstonedesginlogo-removebg-preview.png';
import '../../assets/fontello-plus/fontello-73784b09/css/calander.css';
import '../../assets/fontello-non_view/fontello-cc0cd38b/css/fontello.css';
import '../../assets/fontello-change/fontello-79be5077/css/fontello.css';

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || 'c8c26e34-578c-4112-b3ee-81eadefef144';

const isLocalhost = Boolean(
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
   window.location.hostname === '127.0.0.1' ||
   window.location.hostname === '[::1]')
);

// ─── Validation helpers ────────────────────────────────────────────────────────

// Client-side email format check run before any Supabase call is made. ("auth")
const validateEmail = (email) => {
  const trimmed = (email || '').trim();
  if (!trimmed) return 'Please enter your email address.';
  if (!trimmed.includes('@')) return 'Email must contain an @ symbol.';
  const parts = trimmed.split('@');
  if (parts.length !== 2 || !parts[0] || !parts[1] || !parts[1].includes('.'))
    return 'Please enter a valid email address (e.g. name@example.com).';
  return null;
};

// Enforces password strength rules for new account registrations. ("auth")
const validatePassword = (password) => {
  if (password.length < 8)
    return 'Password must be at least 8 characters long.';
  if (!/[A-Z]/.test(password))
    return 'Password must contain at least one uppercase letter (e.g. A–Z).';
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password))
    return 'Password must contain at least one special character (e.g. !, @, #, $).';
  return null;
};

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * Login Component
 * Handles both user authentication (login) and new user registration (sign up).
 * Uses Supabase auth for managing credentials and sessions.
 */
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Toggle between login and signup modes
  const [isLogin, setIsLogin]                 = useState(true);
  
  // Form input states
  const [email, setEmail]                     = useState('');
  const [username, setUsername]               = useState('');
  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI visibility toggles for passwords
  const [showPassword, setShowPassword]       = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Error, loading, and success states
  const [error, setError]                     = useState('');
  const [fieldErrors, setFieldErrors]         = useState({});
  const [loading, setLoading]                 = useState(false);
  const [success, setSuccess]                 = useState(false);
  
  // Checks if the user just arrived from an email confirmation link
  const [confirmed, setConfirmed]             = useState(
    location.state?.confirmed === true
  );

  // ── Forgot password state ───────────────────────────────────────────────────
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotLoading, setForgotLoading]     = useState(false);
  const [forgotError, setForgotError]         = useState('');
  const [forgotMessage, setForgotMessage]     = useState('');

  // ── hCaptcha state ───────────────────────────────────────────────────────────
  const captchaRef                            = useRef(null);
  const [captchaToken, setCaptchaToken]       = useState('');
  const [captchaError, setCaptchaError]       = useState('');
  const [captchaFailed, setCaptchaFailed]     = useState(false);

  const resetCaptcha = () => {
    try {
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
      }
    } catch (e) {
      console.warn('Could not reset hCaptcha:', e);
    }
    setCaptchaToken('');
    setCaptchaError('');
    setCaptchaFailed(false);
  };

  useEffect(() => {
    // Pre-fill the email/username fields with values saved from the last logout session,
    // so returning users don't need to retype their credentials. ("auth")
    const prefill = getLoginPrefill();
    if (!prefill) return;

    if (prefill.email) {
      setEmail(prefill.email);
    }

    if (prefill.username) {
      setUsername(prefill.username);
    }
  }, []);

  // ── Switch tabs ──────────────────────────────────────────────────────────────
  const switchTab = (loginMode) => {
    setIsLogin(loginMode);
    setError('');
    setSuccess(false);
    setConfirmed(false);
    setFieldErrors({});
    setEmail('');
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setShowForgotPassword(false);
    setForgotError('');
    setForgotMessage('');
    resetCaptcha();
  };

  // ── Client-side validation for signup ───────────────────────────────────────
  const validateSignupFields = () => {
    const errors = {};

    if (!username.trim()) {
      errors.username = 'Username is required.';
    } else if (username.trim().length < 3) {
      errors.username = 'Username must be at least 3 characters.';
    }

    const emailError = validateEmail(email);
    if (emailError) errors.email = emailError;

    const passwordError = validatePassword(password);
    if (passwordError) errors.password = passwordError;

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password.';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    return errors;
  };

  // ── Forgot password submit ──────────────────────────────────────────────────
  // Triggers Supabase's built-in password reset email flow.
  // Uses intentionally vague wording ("if an account exists…") to prevent
  // attackers from using this form to enumerate registered email addresses. ("auth")
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    setCaptchaError('');

    const emailError = validateEmail(email);
    if (emailError) {
      setForgotError(emailError);
      return;
    }

    if (HCAPTCHA_SITE_KEY && !captchaToken) {
      if (isLocalhost && captchaFailed) {
        console.warn('Bypassing hCaptcha on localhost development environment due to provider error.');
      } else {
        setCaptchaError('Please complete the hCaptcha verification before continuing.');
        return;
      }
    }

    setForgotLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        {
          redirectTo: `${window.location.origin}/reset-password`,
          captchaToken: captchaToken || undefined,
        }
      );

      if (resetError) {
        setForgotError(resetError.message || 'Could not send reset email. Please try again.');
        return;
      }

      // Deliberately generic wording — doesn't confirm or deny whether an
      // account exists for this email, to avoid leaking account existence.
      setForgotMessage("If an account exists for that email, we've sent a link to reset your password. Please check your inbox.");
    } catch (unexpectedError) {
      console.error('Unexpected forgot-password error:', unexpectedError);
      setForgotError('An unexpected error occurred. Please try again.');
    } finally {
      setForgotLoading(false);
      resetCaptcha();
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  // Handles both signup and login in one handler — branched by the isLogin flag. ("auth")
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setConfirmed(false);
    setFieldErrors({});
    setCaptchaError('');

    // ── Signup flow ────────────────────────────────────────────────────────────
    // Calls Supabase signUp, which sends a confirmation email before activating the account. ("auth")
    if (!isLogin) {
      const errors = validateSignupFields();
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }

      if (HCAPTCHA_SITE_KEY && !captchaToken) {
        setCaptchaError('Please complete the hCaptcha verification.');
        return;
      }

      setLoading(true);
      try {
        const clientIp = await getUserIp().catch(() => null);
        const { error: authError } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: {
              username: username.trim(),
              ip_address: clientIp || undefined,
            },
            emailRedirectTo: `${window.location.origin}/auth-confirm`,
            captchaToken: captchaToken || undefined,
          },
        });

        if (authError) {
          const rawMsg =
            typeof authError === 'string'
              ? authError
              : authError.message || authError.error_description || authError.msg || '';
          const msgStr = typeof rawMsg === 'object' ? JSON.stringify(rawMsg) : String(rawMsg);

          if (msgStr.toLowerCase().includes('already registered')) {
            setError('An account with this email already exists. Please log in instead.');
          } else if (!msgStr || msgStr === '{}' || msgStr === '[object Object]') {
            setError('Registration failed. Please check your details and try again.');
          } else {
            setError(msgStr);
          }
          return;
        }

        // Store user IP in database
        if (clientIp) {
          Promise.resolve(recordUserIp(email.trim(), clientIp)).catch((ipErr) => {
            console.warn('Signup IP recording note:', ipErr);
          });
        }

        setSuccess(true);
        setIsLogin(true);
        setEmail('');
        setUsername('');
        setPassword('');
        setConfirmPassword('');
      } catch (unexpectedError) {
        console.error('Unexpected signup error:', unexpectedError);
        setError('An unexpected error occurred. Please try again.');
      } finally {
        setLoading(false);
        resetCaptcha();
      }
      return;
    }

    // ── Login flow ─────────────────────────────────────────────────────────────
    // Signs in with Supabase, stores the session token in localStorage,
    // checks admin status, then routes to the appropriate dashboard. ("auth")
    const emailError = validateEmail(email);
    if (emailError) {
      setFieldErrors({ email: emailError });
      return;
    }
    if (!password) {
      setFieldErrors({ password: 'Please enter your password.' });
      return;
    }

    if (HCAPTCHA_SITE_KEY && !captchaToken) {
      if (isLocalhost && captchaFailed) {
        console.warn('Bypassing hCaptcha on localhost development environment due to provider error.');
      } else {
        setCaptchaError('Please complete the hCaptcha verification.');
        return;
      }
    }

    setLoading(true);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
        options: {
          captchaToken: captchaToken || undefined,
        },
      });

      if (authError) {
        const rawMsg =
          typeof authError === 'string'
            ? authError
            : authError.message || authError.error_description || authError.msg || '';
        const msgStr = typeof rawMsg === 'object' ? JSON.stringify(rawMsg) : String(rawMsg);

        if (
          msgStr.toLowerCase().includes('invalid login') ||
          msgStr.toLowerCase().includes('invalid credentials')
        ) {
          setError('Incorrect email or password. Please try again.');
        } else if (msgStr.toLowerCase().includes('email not confirmed')) {
          setError('Please confirm your email address before logging in. Check your inbox.');
        } else if (!msgStr || msgStr === '{}' || msgStr === '[object Object]') {
          setError('Login failed. Please check your credentials and try again.');
        } else {
          setError(msgStr);
        }
        return;
      }

      if (!data?.session) {
        // User exists but hasn't confirmed their email yet — resend the link
        try {
          await supabase.auth.resend({ type: 'signup', email: email.trim() });
          setError("Your email isn't confirmed yet. We've resent the confirmation link — please check your inbox.");
        } catch {
          setError("Your email isn't confirmed yet. Please check your inbox for the confirmation link.");
        }
        return;
      }

      localStorage.setItem('token', data.session.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // ── Admin check against tbl_admin via Supabase RPC ─────────────────────
      // Calls a Postgres function (check_is_admin) that returns only true/false
      // so the password_hash or admin table details are never exposed to the browser. ("admin-auth")
      let isAdmin = false;
      try {
        const role = await resolveAdminRole(data.user.email);
        isAdmin = role.isAdmin;
      } catch (rpcErr) {
        console.error('Admin check failed:', rpcErr);
      }
      localStorage.setItem('isAdmin', JSON.stringify(isAdmin));

      // ── Store user IP address in database ─────────────────────────────────
      try {
        Promise.resolve(recordUserIp(data.user.email)).catch((ipErr) => {
          console.warn('Login IP recording note:', ipErr);
        });
      } catch (ipErr) {
        console.warn('Could not trigger IP recording:', ipErr);
      }

      setSuccess(true);
      setEmail('');
      setPassword('');

      // ── Route based on admin status ────────────────────────────────────────
      // Admins are sent to the admin account dashboard; regular users to /account. ("auth")
      setTimeout(() => {
        navigate(isAdmin ? '/admin-account' : '/account');
      }, 1500);
    } catch (unexpectedError) {
      console.error('Unexpected login error:', unexpectedError);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
      resetCaptcha();
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="auth-page-container" style={styles.container}>
      <div className="auth-card" style={styles.card}>

        <div style={styles.logoRow}>
          <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logoImage} />
        </div>

        <h2 style={styles.title}>Clipstone Miners Welfare</h2>

        <div style={styles.toggleContainer}>
          <button
            type="button"
            onClick={() => switchTab(true)}
            style={isLogin ? styles.activeTab : styles.inactiveTab}
          >
            Login
          </button>
          <button
            type="button"
            onClick={() => switchTab(false)}
            style={!isLogin ? styles.activeTab : styles.inactiveTab}
          >
            Sign Up
          </button>
        </div>

        <p style={styles.subtitle}>
          {showForgotPassword
            ? 'Reset your password'
            : isLogin ? 'Sign in to your account' : 'Create a new account'}
        </p>

        <form id="login-form" onSubmit={showForgotPassword ? handleForgotPasswordSubmit : handleSubmit} style={styles.form} noValidate>

          {/* Arrived from email confirmation link */}
          {confirmed && !showForgotPassword && (
            <div style={styles.successAlert}>
              <i className="icon-calendar" style={styles.alertIcon} /> Email confirmed! You can now sign in below.
            </div>
          )}

          {/* Signup success — awaiting confirmation email */}
          {success && !isLogin && !showForgotPassword && (
            <div style={styles.successAlert}>
              <i className="icon-calendar" style={styles.alertIcon} /> Account created! Please check your email and click the confirmation
              link before signing in.
            </div>
          )}

          {/* Login success */}
          {success && isLogin && !showForgotPassword && (
            <div style={styles.successAlert}>
              <i className="icon-calendar" style={styles.alertIcon} /> Login successful — redirecting...
            </div>
          )}

          {/* Global error */}
          {error && !showForgotPassword && (
            <div style={styles.errorAlert}>
              <strong>{isLogin ? 'Login' : 'Sign up'} failed:</strong> {error}
            </div>
          )}

          {/* Forgot-password banners */}
          {showForgotPassword && forgotError && (
            <div style={styles.errorAlert}>{forgotError}</div>
          )}
          {showForgotPassword && forgotMessage && (
            <div style={styles.successAlert}>{forgotMessage}</div>
          )}

          {/* Username — signup only */}
          {!isLogin && !showForgotPassword && (
            <div style={styles.inputGroup}>
              <label htmlFor="username" style={styles.label}>Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose a username"
                autoComplete="username"
                style={fieldErrors.username
                  ? { ...styles.input, ...styles.inputError }
                  : styles.input}
              />
              {fieldErrors.username && (
                <p style={styles.fieldError}><i className="icon-left-circled" style={styles.fieldErrorIcon} /> {fieldErrors.username}</p>
              )}
            </div>
          )}

          {/* Email — used for login, signup, and forgot-password */}
          <div style={styles.inputGroup}>
            <label htmlFor="email" style={styles.label}>Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoComplete="email"
              style={fieldErrors.email
                ? { ...styles.input, ...styles.inputError }
                : styles.input}
            />
            {fieldErrors.email && !showForgotPassword && (
              <p style={styles.fieldError}><i className="icon-left-circled" style={styles.fieldErrorIcon} /> {fieldErrors.email}</p>
            )}
          </div>

          {!showForgotPassword && (
            <>
              {/* Password */}
              <div style={styles.inputGroup}>
                <label htmlFor="password" style={styles.label}>Password</label>
                <div style={styles.passwordWrapper}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    style={fieldErrors.password
                      ? { ...styles.input, ...styles.inputError }
                      : styles.input}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={styles.toggleButton}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={showPassword ? 'icon-eye' : 'icon-eye-off'} style={styles.icon} />
                  </button>
                </div>
                {fieldErrors.password && (
                  <p style={styles.fieldError}><i className="icon-left-circled" style={styles.fieldErrorIcon} /> {fieldErrors.password}</p>
                )}
                {!isLogin && !fieldErrors.password && (
                  <p style={styles.hint}>
                    Min. 8 characters, one uppercase letter, and one special character (e.g. !@#$).
                  </p>
                )}
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setError('');
                      setFieldErrors({});
                      setForgotError('');
                      setForgotMessage('');
                    }}
                    style={styles.forgotPasswordLink}
                  >
                    Forgot password?
                  </button>
                )}
              </div>

              {/* Confirm password — signup only */}
              {!isLogin && (
                <div style={styles.inputGroup}>
                  <label htmlFor="confirmPassword" style={styles.label}>Confirm Password</label>
                  <div style={styles.passwordWrapper}>
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your password"
                      autoComplete="new-password"
                      style={fieldErrors.confirmPassword
                        ? { ...styles.input, ...styles.inputError }
                        : styles.input}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      style={styles.toggleButton}
                      aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                    >
                      <i className={showConfirmPassword ? 'icon-eye' : 'icon-eye-off'} style={styles.icon} />
                    </button>
                  </div>
                  {fieldErrors.confirmPassword && (
                    <p style={styles.fieldError}><i className="icon-left-circled" style={styles.fieldErrorIcon} /> {fieldErrors.confirmPassword}</p>
                  )}
                </div>
              )}

              {/* hCaptcha Verification */}
              {HCAPTCHA_SITE_KEY && (
                <div style={styles.captchaWrapper}>
                  <div style={styles.captchaInner}>
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={HCAPTCHA_SITE_KEY}
                      onVerify={(token) => {
                        setCaptchaToken(token);
                        setCaptchaError('');
                        setError('');
                      }}
                      onExpire={() => {
                        setCaptchaToken('');
                      }}
                      onError={(err) => {
                        console.error('hCaptcha error:', err);
                        setCaptchaFailed(true);
                      }}
                    />
                  </div>
                  {captchaError && (
                    <p style={styles.fieldError}>
                      <i className="icon-left-circled" style={styles.fieldErrorIcon} /> {captchaError}
                    </p>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || success}
                style={(loading || success)
                  ? { ...styles.button, ...styles.buttonDisabled }
                  : styles.button}
              >
                {loading
                  ? (isLogin ? 'Signing in...' : 'Creating account...')
                  : success
                    ? 'Login successful!'
                    : (isLogin ? 'Sign In' : 'Sign Up')}
              </button>
            </>
          )}

          {showForgotPassword && (
            <>
              <p style={styles.hint}>
                Enter your email and we'll send you a link to reset your password.
              </p>

              {/* hCaptcha Verification */}
              {HCAPTCHA_SITE_KEY && (
                <div style={styles.captchaWrapper}>
                  <div style={styles.captchaInner}>
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={HCAPTCHA_SITE_KEY}
                      onVerify={(token) => {
                        setCaptchaToken(token);
                        setCaptchaError('');
                        setForgotError('');
                      }}
                      onExpire={() => {
                        setCaptchaToken('');
                      }}
                      onError={(err) => {
                        console.error('hCaptcha error:', err);
                        setCaptchaFailed(true);
                      }}
                    />
                  </div>
                  {captchaError && (
                    <p style={styles.fieldError}>
                      <i className="icon-left-circled" style={styles.fieldErrorIcon} /> {captchaError}
                    </p>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={forgotLoading}
                style={forgotLoading
                  ? { ...styles.button, ...styles.buttonDisabled }
                  : styles.button}
              >
                {forgotLoading ? 'Sending...' : 'Send reset link'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotError('');
                  setForgotMessage('');
                  resetCaptcha();
                }}
                style={styles.backToLoginButton}
              >
                Back to login
              </button>
            </>
          )}

        </form>
      </div>
    </div>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '40px 20px',
    backgroundColor: '#f9f8f6',
    background: 'linear-gradient(135deg, #f9f8f6 0%, #e8e6e1 100%)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: '#ffffff',
    padding: 'clamp(22px, 5vw, 50px)',
    borderRadius: '16px',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '500px',
    border: '1px solid #e5e5e5',
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
    backgroundColor: '#1e3f32',
    padding: 'clamp(12px, 4vw, 20px)',
    borderRadius: '12px',
  },
  logoImage: {
    height: 'clamp(72px, 16vw, 120px)',
    width: 'auto',
  },
  title: {
    margin: '0 0 24px 0',
    textAlign: 'center',
    color: '#1e3f32',
    fontSize: '24px',
    fontWeight: '700',
  },
  toggleContainer: {
    display: 'flex',
    marginBottom: '32px',
    borderBottom: '1px solid #d1d5db',
  },
  activeTab: {
    flex: 1,
    padding: '12px',
    border: 'none',
    borderBottom: '4px solid #1e3f32',
    backgroundColor: 'transparent',
    color: '#1e3f32',
    fontWeight: '700',
    fontSize: 'clamp(16px, 4.8vw, 20px)',
    cursor: 'pointer',
  },
  inactiveTab: {
    flex: 1,
    padding: '12px',
    border: 'none',
    borderBottom: '4px solid transparent',
    backgroundColor: 'transparent',
    color: '#6b7280',
    fontWeight: '600',
    fontSize: 'clamp(16px, 4.8vw, 20px)',
    cursor: 'pointer',
  },
  subtitle: {
    margin: '0 0 24px 0',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 'clamp(14px, 3.8vw, 16px)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputGroup: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    color: '#374151',
    fontSize: '16px',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  passwordWrapper: {
    position: 'relative',
  },
  toggleButton: {
    position: 'absolute',
    top: '50%',
    right: '12px',
    transform: 'translateY(-50%)',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '18px',
    padding: '0',
    color: '#4b5563',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    fontSize: '18px',
    lineHeight: '1',
  },
  inputError: {
    border: '1px solid #dc2626',
    backgroundColor: '#fff8f8',
  },
  fieldError: {
    margin: '6px 0 0 0',
    color: '#dc2626',
    fontSize: '13px',
  },
  hint: {
    margin: '6px 0 0 0',
    color: '#6b7280',
    fontSize: '12px',
  },
  forgotPasswordLink: {
    display: 'block',
    marginTop: '10px',
    background: 'transparent',
    border: 'none',
    color: '#1e3f32',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '0',
    textDecoration: 'underline',
  },
  backToLoginButton: {
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '10px 0 0 0',
    textAlign: 'center',
  },
  button: {
    backgroundColor: '#1e3f32',
    color: '#ffffff',
    padding: '14px 16px',
    border: 'none',
    borderRadius: '8px',
    fontSize: 'clamp(16px, 4vw, 18px)',
    fontWeight: '600',
    cursor: 'pointer',
    letterSpacing: '0.02em',
    marginTop: '8px',
  },
  buttonDisabled: {
    backgroundColor: '#6b7280',
    cursor: 'not-allowed',
  },
  errorAlert: {
    backgroundColor: '#fff0f0',
    color: '#dc2626',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '20px',
    border: '1px solid #fca5a5',
    lineHeight: '1.5',
  },
  successAlert: {
    backgroundColor: '#ecfdf5',
    color: '#059669',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '20px',
    border: '1px solid #6ee7b7',
  },
  captchaWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '16px 0 10px 0',
  },
  captchaInner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: '78px',
    overflowX: 'auto',
  },
  fieldErrorIcon: {
    marginRight: '6px',
    verticalAlign: 'middle',
  },
};

export default Login;